import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, ChevronLeft, Smartphone, CreditCard, Gift, Coffee, ShoppingBag, Users, TrendingUp, Zap, Shield, ArrowRight, Check, Menu, X, Star, Clock, Globe } from 'lucide-react';

// TypeScript 인터페이스 정의
interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
}

interface PricingPlan {
  name: string;
  price: string;
  features: string[];
  popular?: boolean;
}

interface Benefit {
  number: string;
  label: string;
}

interface Industry {
  icon: React.ReactNode;
  name: string;
}

interface CarouselSlide {
  icon: React.ReactNode;
  title: string;
  description: string;
  features: string[];
}

const Landing: React.FC = () => {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const handleScroll = (): void => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % 3);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const features: Feature[] = [
    {
      icon: <Users className="w-6 h-6" />,
      title: "멤버십 관리",
      description: "포인트 적립, 등급별 혜택, VIP 프로그램을 한 번에 관리하세요"
    },
    {
      icon: <Smartphone className="w-6 h-6" />,
      title: "모바일 오더",
      description: "줄 서지 않고 미리 주문하는 스마트한 주문 시스템"
    },
    {
      icon: <CreditCard className="w-6 h-6" />,
      title: "선불카드",
      description: "충전식 페이 시스템으로 빠르고 간편한 결제 경험"
    },
    {
      icon: <Gift className="w-6 h-6" />,
      title: "선물하기",
      description: "모바일 쿠폰과 기프티콘으로 마음을 전하세요"
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "데이터 분석",
      description: "고객 행동 패턴과 매출 데이터를 실시간으로 분석"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "보안 결제",
      description: "PCI-DSS 인증 보안으로 안전한 거래 보장"
    }
  ];

  const pricing: PricingPlan[] = [
    { 
      name: "Starter", 
      price: "₩99,000", 
      features: [
        "월 주문 1,000건",
        "기본 멤버십 기능",
        "선불카드 시스템",
        "기본 분석 리포트",
        "이메일 지원"
      ] 
    },
    { 
      name: "Business", 
      price: "₩299,000", 
      features: [
        "월 주문 10,000건",
        "고급 멤버십 관리",
        "선물하기 기능",
        "AI 데이터 분석",
        "우선 지원",
        "멀티 매장 관리"
      ],
      popular: true
    },
    { 
      name: "Enterprise", 
      price: "맞춤 견적", 
      features: [
        "무제한 주문",
        "전체 기능 이용",
        "API 연동",
        "전담 매니저",
        "맞춤 개발",
        "SLA 보장"
      ] 
    }
  ];

  const benefits: Benefit[] = [
    { number: "30%", label: "평균 재방문율 증가" },
    { number: "2.5x", label: "객단가 상승" },
    { number: "60%", label: "대기시간 단축" },
    { number: "45%", label: "운영비용 절감" }
  ];

  const industries: Industry[] = [
    { icon: <Coffee className="w-8 h-8" />, name: "카페" },
    { icon: <ShoppingBag className="w-8 h-8" />, name: "리테일" },
    { icon: <Users className="w-8 h-8" />, name: "프랜차이즈" },
    { icon: <Star className="w-8 h-8" />, name: "레스토랑" }
  ];

  const carouselSlides: CarouselSlide[] = [
    {
      icon: <Smartphone className="w-8 h-8 text-gray-700" />,
      title: "모바일 앱 자동 생성",
      description: "브랜드 아이덴티티를 반영한 iOS/Android 앱을 5분 만에 배포하세요. 복잡한 개발 과정 없이 드래그 앤 드롭으로 간편하게 커스터마이징 가능합니다.",
      features: ["즉시 적용 가능", "무료 업데이트"]
    },
    {
      icon: <Clock className="w-8 h-8 text-gray-700" />,
      title: "실시간 주문 관리",
      description: "POS 연동으로 주문부터 결제까지 원스톱 처리합니다. 실시간 알림으로 주문을 놓치지 않고, 대기 시간을 60% 단축할 수 있습니다.",
      features: ["평균 처리 30초", "자동 재고 연동"]
    },
    {
      icon: <TrendingUp className="w-8 h-8 text-gray-700" />,
      title: "데이터 기반 성장",
      description: "AI 분석으로 고객 인사이트를 발견하고 매출을 최적화합니다. 실시간 대시보드로 비즈니스 현황을 한눈에 파악할 수 있습니다.",
      features: ["실시간 분석", "맞춤형 리포트"]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <img src="/flos-icon.svg" alt="Flos" className="w-8 h-8 mr-2" />
              <span className="text-xl font-semibold text-gray-900">Flos</span>
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-gray-600 hover:text-gray-900 transition-colors">기능</a>
              <a href="#benefits" className="text-gray-600 hover:text-gray-900 transition-colors">효과</a>
              <a href="#pricing" className="text-gray-600 hover:text-gray-900 transition-colors">요금</a>
              <a href="#" className="text-gray-600 hover:text-gray-900 transition-colors">고객사례</a>
              <a href="#" className="text-gray-600 hover:text-gray-900 transition-colors">문서</a>
              <button 
                onClick={() => navigate('/auth/login')}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
              >
                로그인
              </button>
              <button 
                onClick={() => navigate('/accounts/create')}
                className="px-4 py-2 bg-gray-900 text-white rounded hover:bg-gray-800 transition-colors"
              >
                무료 체험
              </button>
            </div>

            <button 
              className="md:hidden"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t">
            <div className="px-2 pt-2 pb-3 space-y-1">
              <a href="#features" className="block px-3 py-2 text-gray-600 hover:text-gray-900">기능</a>
              <a href="#benefits" className="block px-3 py-2 text-gray-600 hover:text-gray-900">효과</a>
              <a href="#pricing" className="block px-3 py-2 text-gray-600 hover:text-gray-900">요금</a>
              <a href="#" className="block px-3 py-2 text-gray-600 hover:text-gray-900">고객사례</a>
              <a href="#" className="block px-3 py-2 text-gray-600 hover:text-gray-900">문서</a>
              <div className="pt-2 space-y-2">
                <button 
                  onClick={() => navigate('/auth/login')}
                  className="w-full px-4 py-2 text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
                >
                  로그인
                </button>
                <button 
                  onClick={() => navigate('/accounts/create')}
                  className="w-full px-4 py-2 bg-gray-900 text-white rounded hover:bg-gray-800"
                >
                  무료 체험
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-sm font-medium mb-6">
              <Zap className="w-4 h-4 mr-1" />
              5분 만에 모바일 앱 런칭
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              당신의 비즈니스를 위한<br />
              <span className="text-blue-600">올인원 모바일 커머스 플랫폼</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              멤버십, 모바일 오더, 선불카드, 선물하기까지<br />
              고객 경험을 혁신하는 통합 솔루션을 제공합니다
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
               <button 
                 onClick={() => navigate('/accounts/create')}
                 className="px-8 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors flex items-center justify-center group"
               >
                무료로 시작하기
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <button className="px-8 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                데모 예약하기
              </button>
            </div>
            <p className="mt-4 text-sm text-gray-500">
              ✓ 카드 등록 불필요 • ✓ 14일 무료 체험 • ✓ 언제든 취소 가능
            </p>
          </div>

          {/* App Preview - Carousel */}
          <div className="mt-20 relative">
            <div className="max-w-4xl mx-auto px-12">
              <div className="relative">
                <div className="overflow-hidden py-4">
                  <div 
                    className="flex transition-transform duration-500 ease-in-out"
                    style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                  >
                    {carouselSlides.map((slide, index) => (
                      <div key={index} className="w-full flex-shrink-0 px-4">
                        <div className="bg-white rounded-2xl border border-gray-200 p-12">
                          <div className="flex items-center justify-between">
                            <div className="flex-1 pr-8">
                              <div className="inline-flex items-center justify-center w-14 h-14 bg-gray-100 rounded-xl mb-6">
                                {slide.icon}
                              </div>
                              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                                {slide.title}
                              </h3>
                              <p className="text-gray-600 mb-6 leading-relaxed">
                                {slide.description}
                              </p>
                              <div className="flex items-center space-x-4">
                                {slide.features.map((feature, idx) => (
                                  <div key={idx} className="flex items-center text-sm text-gray-500">
                                    <Check className="w-5 h-5 mr-2 text-green-500" />
                                    <span>{feature}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                             <div className="hidden md:block">
                               <div className="w-64 h-48 bg-gradient-to-br from-gray-100 to-gray-50 rounded-xl flex items-center justify-center">
                                 <div className="w-24 h-24 text-gray-300">
                                   {slide.icon}
                                 </div>
                               </div>
                             </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Navigation Arrows */}
                 <button
                   onClick={(): void => setCurrentSlide((prev) => (prev - 1 + 3) % 3)}
                   className="absolute left-0 top-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors z-10"
                 >
                   <ChevronLeft className="w-5 h-5 text-gray-600" />
                 </button>
                 <button
                   onClick={(): void => setCurrentSlide((prev) => (prev + 1) % 3)}
                   className="absolute right-0 top-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors z-10"
                 >
                   <ChevronRight className="w-5 h-5 text-gray-600" />
                 </button>

                {/* Carousel Indicators */}
                <div className="flex justify-center mt-8 space-x-2">
                  {[0, 1, 2].map((index) => (
                    <button
                      key={index}
                      onClick={(): void => setCurrentSlide(index)}
                      className={`h-2 rounded-full transition-all duration-300 ${
                        currentSlide === index 
                          ? 'w-8 bg-gray-900' 
                          : 'w-2 bg-gray-300 hover:bg-gray-400'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              모든 업종에 최적화된 솔루션
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {industries.map((industry, idx) => (
              <div key={idx} className="text-center">
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3 hover:bg-gray-200 transition-colors">
                  {industry.icon}
                </div>
                <span className="text-gray-700 font-medium">{industry.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              검증된 비즈니스 성과
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              1,000개 이상의 매장이 Flos와 함께 성장하고 있습니다
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {benefits.map((benefit, idx) => (
              <div key={idx} className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">{benefit.number}</div>
                <div className="text-gray-600">{benefit.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              하나의 플랫폼, 모든 기능
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              복잡한 시스템 연동 없이 필요한 모든 기능을 한 곳에서
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, idx) => (
              <div key={idx} className="p-6 border border-gray-200 rounded-lg hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mb-4 text-gray-700">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>

          {/* Integration Partners */}
          <div className="mt-16 text-center">
            <p className="text-sm text-gray-500 mb-8">주요 POS 및 결제 시스템과 연동</p>
            <div className="flex flex-wrap justify-center gap-8 items-center">
              <div className="w-32 h-12 bg-gray-200 rounded flex items-center justify-center">
                <span className="text-gray-600 font-medium">POS 연동</span>
              </div>
              <div className="w-32 h-12 bg-gray-200 rounded flex items-center justify-center">
                <span className="text-gray-600 font-medium">카드사 연동</span>
              </div>
              <div className="w-32 h-12 bg-gray-200 rounded flex items-center justify-center">
                <span className="text-gray-600 font-medium">배달 플랫폼</span>
              </div>
              <div className="w-32 h-12 bg-gray-200 rounded flex items-center justify-center">
                <span className="text-gray-600 font-medium">ERP 시스템</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              비즈니스 규모에 맞는 요금제
            </h2>
            <p className="text-lg text-gray-600">
              14일 무료 체험으로 시작하세요
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {pricing.map((plan, idx) => (
              <div key={idx} className={`bg-white rounded-lg border ${plan.popular ? 'border-blue-500 shadow-xl relative' : 'border-gray-200'} p-8`}>
                {plan.popular && (
                  <span className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-blue-500 text-white px-4 py-1 rounded-full text-sm">
                    인기
                  </span>
                )}
                <h3 className="text-2xl font-semibold text-gray-900">{plan.name}</h3>
                <div className="mt-4 mb-8">
                  <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                  {plan.price !== "맞춤 견적" && <span className="text-gray-600">/월</span>}
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, fIdx) => (
                    <li key={fIdx} className="flex items-start">
                      <Check className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button 
                  onClick={() => navigate('/accounts/create')}
                  className={`w-full py-3 rounded-lg transition-colors ${
                    plan.popular 
                      ? 'bg-blue-500 text-white hover:bg-blue-600' 
                      : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {plan.price === "맞춤 견적" ? "상담 신청" : "시작하기"}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-gray-900 to-gray-700">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-4">
            지금 시작하고 매출을 늘리세요
          </h2>
          <p className="text-lg text-gray-300 mb-8">
            설정부터 운영까지 전문가가 함께합니다.<br />
            14일 무료 체험으로 부담 없이 시작하세요.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
             <button 
               onClick={() => navigate('/accounts/create')}
               className="px-8 py-3 bg-white text-gray-900 rounded-lg hover:bg-gray-100 transition-colors inline-flex items-center justify-center group"
             >
              무료 체험 시작
              <ChevronRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <button className="px-8 py-3 border border-white text-white rounded-lg hover:bg-white/10 transition-colors">
              영업팀 문의
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-white border-t">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-5 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center mb-4">
                <img src="/flos-icon.svg" alt="Flos" className="w-6 h-6 mr-2" />
                <span className="text-lg font-semibold text-gray-900">Flos</span>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                모바일 커머스의 미래를 만들어갑니다
              </p>
              <div className="flex space-x-4">
                <Globe className="w-5 h-5 text-gray-400 hover:text-gray-600 cursor-pointer" />
                <Gift className="w-5 h-5 text-gray-400 hover:text-gray-600 cursor-pointer" />
                <Users className="w-5 h-5 text-gray-400 hover:text-gray-600 cursor-pointer" />
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">제품</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><a href="#" className="hover:text-gray-900">멤버십</a></li>
                <li><a href="#" className="hover:text-gray-900">모바일오더</a></li>
                <li><a href="#" className="hover:text-gray-900">선불카드</a></li>
                <li><a href="#" className="hover:text-gray-900">선물하기</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">리소스</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><a href="#" className="hover:text-gray-900">가이드</a></li>
                <li><a href="#" className="hover:text-gray-900">API 문서</a></li>
                <li><a href="#" className="hover:text-gray-900">고객사례</a></li>
                <li><a href="#" className="hover:text-gray-900">블로그</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">회사</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><a href="#" className="hover:text-gray-900">소개</a></li>
                <li><a href="#" className="hover:text-gray-900">채용</a></li>
                <li><a href="#" className="hover:text-gray-900">파트너</a></li>
                <li><a href="#" className="hover:text-gray-900">문의</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-sm text-gray-600">
              © 2025 Flos. All rights reserved.
            </p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <a href="#" className="text-sm text-gray-600 hover:text-gray-900">개인정보처리방침</a>
              <a href="#" className="text-sm text-gray-600 hover:text-gray-900">이용약관</a>
              <a href="#" className="text-sm text-gray-600 hover:text-gray-900">쿠키정책</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;